import { FC, useState } from "react"
import { useHistory, useRouteMatch } from "react-router-dom"
import { initialCustomerPartQueryParams, usePartCategories, usePartChemicals } from "@ncs/ncs-api"
import { ShopBarNavSubmenu } from "./sub-menu/ShopBarNavSubmenu"
import {
	AnimatedEntrance,
	encodeUrlState,
	Icon,
	TrackingEvent,
	useUrlState,
	Flag,
	useIsFeatureFlagOn,
} from "@ncs/web-legos"
import { PartSearchResultsUrlState } from "~/views/shop/part-search-results"
import { useAnalytics } from "~/hooks/useAnalytics"
import { navLinkButton, navListStyle, submenuContainerStyle } from "./styles"

export type ShopBarNavUrlState = Pick<
	PartSearchResultsUrlState,
	"categories" | "manufacturers" | "systems" | "featured" | "clearance"
>

export const ShopBarNav: FC = () => {
	const history = useHistory()
	const [categories] = usePartCategories()
	const [chemicals] = usePartChemicals()
	// eslint-disable-next-line
	const [showCategoriesSubmenu, setShowCategoriesSubmenu] = useState(false)
	const [showChemicalsSubmenu, setShowChemicalsSubmenu] = useState(false)
	const { trackEvent } = useAnalytics()
	const hideTrending = useIsFeatureFlagOn(Flag.VITE_FLAG_HIDE_TRENDING)

	const [, { setUrlState }] = useUrlState<ShopBarNavUrlState>({
		categories: initialCustomerPartQueryParams.categories,
		manufacturers: initialCustomerPartQueryParams.manufacturers,
		systems: initialCustomerPartQueryParams.systems,
		featured: null,
		clearance: null,
	})
	const isOnSearchPage = useRouteMatch("/shop/search")

	const viewResult = (urlState: Partial<PartSearchResultsUrlState>) => {
		if (isOnSearchPage) {
			setUrlState({
				...initialCustomerPartQueryParams,
				...urlState,
			})
		} else {
			history.push({
				pathname: "/shop/search",
				search: encodeUrlState<PartSearchResultsUrlState>(urlState),
			})
		}
	}

	const onViewCategory = (categoryId: string) => {
		setShowCategoriesSubmenu(false)
		const urlState: Partial<PartSearchResultsUrlState> = {
			categories: [categoryId],
			page: 1,
		}
		viewResult(urlState)
	}

	const clearanceHandler = () => {
		viewResult({ clearance: true, page: 1 })
		trackEvent(TrackingEvent.CLEARANCE_NAV_BAR)
	}

	const trandingHandler = () => {
		viewResult({ featured: true, page: 1 })
		trackEvent(TrackingEvent.TRENDING)
	}

	const chemicalsSubmenuHandler = () => {
		setShowChemicalsSubmenu(true)
		trackEvent(TrackingEvent.CHEMISTRY_CATEGORY)
	}

	const partsSubmenuHandler = () => {
		setShowCategoriesSubmenu(true)
		trackEvent(TrackingEvent.PARTS_CATEGORY)
	}

	return (
		<ul css={navListStyle}>
			<li
				className="has-children"
				onMouseEnter={partsSubmenuHandler}
				onMouseLeave={() => setShowCategoriesSubmenu(false)}
			>
				<span>
					<span>Parts</span>
					<span style={{ marginLeft: "5px" }}>
						<Icon icon="chevron-down" />
					</span>
				</span>
				<AnimatedEntrance
					show={showCategoriesSubmenu}
					css={submenuContainerStyle}
					duration={400}
					delay={400}
				>
					<ShopBarNavSubmenu
						items={(categories ?? []).map((category) => ({
							label: category.name ?? category.id,
							onClickValue: category.id,
						}))}
						onClick={onViewCategory}
					/>
				</AnimatedEntrance>
			</li>
			<li
				className="has-children"
				onMouseEnter={chemicalsSubmenuHandler}
				onMouseLeave={() => setShowChemicalsSubmenu(false)}
			>
				<span>
					<span>Chemicals</span>
					<span style={{ marginLeft: "5px" }}>
						<Icon icon="chevron-down" />
					</span>
				</span>
				<AnimatedEntrance
					show={showChemicalsSubmenu}
					css={submenuContainerStyle}
					duration={400}
					delay={400}
				>
					<ShopBarNavSubmenu
						columnCount={2}
						items={(chemicals ?? []).map((chemical) => ({
							label: chemical.name ?? chemical.id,
							onClickValue: chemical.id,
						}))}
						onClick={onViewCategory}
					/>
				</AnimatedEntrance>
			</li>
			{!hideTrending && (
				<li>
					<button css={navLinkButton} onClick={trandingHandler}>
						<span>Trending</span>
					</button>
				</li>
			)}
			<li>
				<button css={navLinkButton} onClick={clearanceHandler}>
					<span>Clearance</span>
				</button>
			</li>
		</ul>
	)
}
